import {
  assertServiceTransition,
  calculateServiceEstimateTotals,
  normalizePaperReturnInput,
  normalizePlanServiceJobInput,
  normalizeServiceSearchInput,
  priceServiceEstimateLines,
  serviceStatusBucket,
  validateServiceEstimateInput,
  type CaptureServicePaperReturnInput,
  type CreateServicePrintInput,
  type MarkServicePrintInput,
  type PlannableServiceOrder,
  type PlanServiceJobInput,
  type SaveServiceEstimateInput,
  type ServiceEstimateRecord,
  type ServiceGateway,
  type ServiceJobRecord,
  type ServiceOverview,
  type ServicePage,
  type ServiceStatus,
  type ServicePrintRecord,
  type ServiceSearchInput,
  type ServiceTechnicianOption,
  type SetServiceApprovalInput,
  type TransitionServiceJobInput,
  type UvvDueRecord,
} from '@forq/service-data';

const technicians: readonly ServiceTechnicianOption[] = [
  { id: 'tech-fb', displayName: 'f.befeld', active: true },
  { id: 'tech-fb2', displayName: 'f.brueno', active: true },
  { id: 'tech-gk', displayName: 'g.kaiser', active: true },
  { id: 'tech-kb', displayName: 'k.befeld', active: true },
  { id: 'tech-mj', displayName: 'm.juckenborg', active: true },
];

function now(): string {
  return new Date().toISOString();
}

function clone<T>(value: T): T {
  return structuredClone(value);
}

function address(companyName: string, street: string, postalCode: string, city: string) {
  return { companyName, contactName: '', street, postalCode, city, countryCode: 'DE' } as const;
}

function seedJob(
  tenantId: string,
  suffix: string,
  patch: Partial<ServiceJobRecord>,
): ServiceJobRecord {
  const createdAt = '2026-07-14T06:00:00.000Z';
  const status = patch.status ?? 'planned';
  return {
    id: `${tenantId}-service-${suffix}`,
    tenantId,
    orderId: `${tenantId}-order-${suffix}`,
    orderNumber: `2026${tenantId === 'ftw' ? '001' : tenantId === 'stw' ? '002' : '003'}${suffix}`,
    serviceNumber: `S-26-${suffix.padStart(6, '0')}`,
    title: 'Serviceauftrag',
    executionMode: 'field',
    kind: 'repair',
    status,
    priority: 'normal',
    customerId: `${tenantId}-customer-${suffix}`,
    customerNumber: `${tenantId === 'ftw' ? '1' : tenantId === 'stw' ? '2' : '3'}${suffix.padStart(4, '0')}`,
    customerName: 'Musterkunde GmbH',
    contactName: 'Herr Müller',
    contactPhone: '02922 123456',
    contactEmail: 'technik@musterkunde.de',
    machineId: `${tenantId}-machine-${suffix}`,
    machineNumber: `${tenantId === 'ftw' ? '1' : tenantId === 'stw' ? '2' : '3'}${(14000 + Number(suffix)).toString()}`,
    machineLabel: 'Toyota 8FBE16T',
    serialNumber: `8FBE-${suffix}`,
    address: address('Musterkunde GmbH', 'Industriestraße 12', '59457', 'Werl'),
    scheduledStart: '2026-07-15T08:00:00',
    scheduledEnd: '2026-07-15T10:00:00',
    technicianName: 'g.kaiser',
    workDescription: 'Gerät prüfen, Fehler feststellen und Reparatur durchführen.',
    internalNotes: '',
    approval: {
      status: 'not-required', approvedAmount: null, decidedAt: null, decidedByName: '', channel: null, notes: '',
    },
    estimate: null,
    latestPrint: null,
    paperReturn: null,
    createdAt,
    updatedAt: createdAt,
    ...patch,
    bucket: serviceStatusBucket(status),
  };
}

function createSeed(): ServiceJobRecord[] {
  const ftw: ServiceJobRecord[] = [
    seedJob('ftw', '24', {
      serviceNumber: 'S-26-000024', orderNumber: '202600132', customerName: 'Frederick Befeld',
      customerNumber: '10024', machineId: null, machineNumber: '', machineLabel: 'Gerät noch nicht bekannt', serialNumber: '',
      status: 'paper-returned', technicianName: 'f.befeld', scheduledStart: '2026-07-14T08:00:00', scheduledEnd: '2026-07-14T09:30:00',
      workDescription: 'Störung vor Ort prüfen. Kunde meldet Aussetzer beim Fahren.',
    }),
    seedJob('ftw', '12', {
      serviceNumber: 'S-26-000012', orderNumber: '202600128', customerName: 'Mister Druck', customerNumber: '10012',
      machineLabel: 'Linde E18-01', machineNumber: '14226', serialNumber: 'H2X386J04567', kind: 'maintenance',
      status: 'billing-ready', technicianName: 'k.befeld', scheduledStart: '2026-07-14T09:00:00', scheduledEnd: '2026-07-14T12:00:00',
      workDescription: 'Wartung nach Herstellervorgabe inklusive Sicht- und Funktionsprüfung.',
    }),
    seedJob('ftw', '09', {
      serviceNumber: 'S-26-000009', orderNumber: '202600121', customerName: 'Jürg Hagedorn', customerNumber: '10009',
      machineLabel: 'Toyota 8FBE16T', machineNumber: '14209', serialNumber: '8FBE16-4711', kind: 'uvv',
      status: 'issued', technicianName: 'g.kaiser', scheduledStart: '2026-07-15T08:00:00', scheduledEnd: '2026-07-15T10:00:00',
      workDescription: 'UVV-Prüfung durchführen und festgestellte Mängel auf dem Papierauftrag dokumentieren.',
      latestPrint: {
        id: 'ftw-print-09-1', serviceJobId: 'ftw-service-09', documentNumber: 'WA-26-000009', version: 1,
        status: 'printed', createdAt: '2026-07-13T14:00:00.000Z', printedAt: '2026-07-13T14:02:00.000Z',
        snapshot: {
          orderNumber: '202600121', serviceNumber: 'S-26-000009', title: 'Serviceauftrag', executionMode: 'field', kind: 'uvv',
          customerNumber: '10009', customerName: 'Jürg Hagedorn', contactName: 'Herr Hagedorn', contactPhone: '02922 99999', contactEmail: '',
          address: address('Jürg Hagedorn', 'Am Markt 4', '59457', 'Werl'), machineNumber: '14209', machineLabel: 'Toyota 8FBE16T',
          serialNumber: '8FBE16-4711', scheduledStart: '2026-07-15T08:00:00', scheduledEnd: '2026-07-15T10:00:00', technicianName: 'g.kaiser',
          workDescription: 'UVV-Prüfung durchführen und festgestellte Mängel dokumentieren.', internalNotes: '',
          printedFields: ['Ankunft', 'Abfahrt', 'Arbeitszeit', 'Fahrtzeit', 'Kilometer', 'Tätigkeiten', 'Material', 'Kundenunterschrift'],
        },
      },
    }),
    seedJob('ftw', '07', {
      serviceNumber: 'S-26-000007', orderNumber: '202600117', customerName: 'Hans Meiser', customerNumber: '10007',
      machineLabel: 'BT RRE160', machineNumber: '14197', serialNumber: 'BT-RRE-160-77',
      status: 'planned', technicianName: '', scheduledStart: '2026-07-15T15:30:00', scheduledEnd: '2026-07-15T17:00:00',
      workDescription: 'Hydraulikfunktion prüfen. Fehler 2:161 laut Kundenmeldung.',
    }),
    seedJob('ftw', '06', {
      serviceNumber: 'S-26-000006', orderNumber: '202600113', customerName: 'Kramer Logistik GmbH', customerNumber: '10006',
      machineLabel: 'Jungheinrich EFG216', machineNumber: '14186', serialNumber: 'FN456789', executionMode: 'workshop',
      status: 'approval-pending', technicianName: 'm.juckenborg', scheduledStart: '2026-07-16T07:30:00', scheduledEnd: null,
      workDescription: 'Defekt prüfen, Arbeitszeit und Ersatzteile kalkulieren und Kundenfreigabe einholen.',
      approval: { status: 'pending', approvedAmount: 1450, decidedAt: null, decidedByName: '', channel: null, notes: 'Kostenschätzung telefonisch angekündigt.' },
    }),
  ];
  return [
    ...ftw,
    seedJob('stw', '01', { serviceNumber: 'S-26-200001', orderNumber: '202620001', customerName: 'Spedition Muster GmbH' }),
    seedJob('gsl', '01', { serviceNumber: 'S-26-300001', orderNumber: '202630001', customerName: 'Sauerland Industrie GmbH' }),
  ];
}

function snapshotOf(job: ServiceJobRecord): ServicePrintRecord['snapshot'] {
  return {
    orderNumber: job.orderNumber,
    serviceNumber: job.serviceNumber,
    title: job.title,
    executionMode: job.executionMode,
    kind: job.kind,
    customerNumber: job.customerNumber,
    customerName: job.customerName,
    contactName: job.contactName,
    contactPhone: job.contactPhone,
    contactEmail: job.contactEmail,
    address: job.address,
    machineNumber: job.machineNumber,
    machineLabel: job.machineLabel,
    serialNumber: job.serialNumber,
    scheduledStart: job.scheduledStart,
    scheduledEnd: job.scheduledEnd,
    technicianName: job.technicianName,
    workDescription: job.workDescription,
    internalNotes: job.internalNotes,
    printedFields: ['Ankunft', 'Abfahrt', 'Arbeitszeit', 'Fahrtzeit', 'Kilometer', 'Tätigkeiten', 'Material', 'Ergebnis', 'Kundenunterschrift', 'Technikerunterschrift'],
  };
}

// Ungeplante service-Vorgaenge je Mandant: die „Zu planen"-Liste. Sobald einer per planJob
// eingeplant wird, entsteht ein Job mit derselben orderId -> der Eintrag faellt automatisch raus.
function createPlannableSeed(): Record<string, PlannableServiceOrder[]> {
  return {
    ftw: [
      {
        orderId: 'ftw-order-plan-1', orderNumber: '202600140', title: 'Hydraulikleckage prüfen',
        customerId: 'ftw-customer-plan-1', customerNumber: '10021', customerName: 'Rheinland Logistik GmbH',
        contactName: 'Herr Sauer', machineId: 'ftw-machine-plan-1', machineNumber: '14233', machineLabel: 'Linde H25',
        address: address('Rheinland Logistik GmbH', 'Industriestraße 12', '59457', 'Werl'),
        createdAt: '2026-07-22T08:00:00.000Z',
      },
      {
        orderId: 'ftw-order-plan-2', orderNumber: '202600141', title: 'UVV-Prüfung fällig',
        customerId: 'ftw-customer-plan-2', customerNumber: '10034', customerName: 'Möller Spedition',
        contactName: 'Frau Klein', machineId: 'ftw-machine-plan-2', machineNumber: '14251', machineLabel: 'Jungheinrich EFG 216',
        address: address('Möller Spedition', 'Am Hafen 4', '59755', 'Arnsberg'),
        createdAt: '2026-07-23T09:30:00.000Z',
      },
    ],
    stw: [],
    gsl: [],
  };
}

function createUvvDueSeed(): Record<string, UvvDueRecord[]> {
  const dayMs = 86_400_000;
  // Relativ zu heute, damit die Liste (überfällig / diesen Monat / nächste Monate) immer etwas zeigt.
  const isoDate = (offsetDays: number) => new Date(Date.now() + offsetDays * dayMs).toISOString().slice(0, 10);
  const isoStamp = (offsetDays: number) => new Date(Date.now() + offsetDays * dayMs).toISOString();
  return {
    ftw: [
      {
        machineId: 'ftw-machine-plan-1', machineNumber: '14233', manufacturer: 'Linde', model: 'H25', typeName: 'Dieselstapler',
        nextUvvDate: isoDate(-8), lastUvvDate: isoDate(-373),
        customerId: 'ftw-customer-plan-1', customerNumber: '10021', customerName: 'Rheinland Logistik GmbH',
        phone: '02922 98120', street: 'Industriestraße 12', postalCode: '59457', city: 'Werl',
        lastJobId: 'ftw-job-uvv-1', lastJobNumber: 'S-202600087', lastJobKind: 'uvv', lastJobDate: isoStamp(-373),
      },
      {
        machineId: 'ftw-machine-plan-2', machineNumber: '14251', manufacturer: 'Jungheinrich', model: 'EFG 216', typeName: 'Elektro-Stapler',
        nextUvvDate: isoDate(18), lastUvvDate: isoDate(-347),
        customerId: 'ftw-customer-plan-2', customerNumber: '10034', customerName: 'Möller Spedition',
        phone: '02931 54700', street: 'Am Hafen 4', postalCode: '59755', city: 'Arnsberg',
        lastJobId: 'ftw-job-uvv-2', lastJobNumber: 'S-202600091', lastJobKind: 'maintenance', lastJobDate: isoStamp(-347),
      },
      {
        machineId: 'ftw-machine-plan-3', machineNumber: '14260', manufacturer: 'Toyota', model: '8FBE16T', typeName: 'Elektro-Stapler',
        nextUvvDate: isoDate(52), lastUvvDate: isoDate(-313),
        customerId: 'ftw-customer-plan-3', customerNumber: '10048', customerName: 'Sauerland Getränke KG',
        phone: '02932 12345', street: 'Lagerweg 8', postalCode: '59821', city: 'Arnsberg',
        lastJobId: null, lastJobNumber: '', lastJobKind: '', lastJobDate: null,
      },
    ],
    stw: [],
    gsl: [],
  };
}

export class DemoServiceGateway implements ServiceGateway {
  private jobs = createSeed();
  private prints: ServicePrintRecord[] = this.jobs.flatMap((job) => job.latestPrint ? [job.latestPrint] : []);
  private plannableOrders = createPlannableSeed();
  private uvvDue = createUvvDueSeed();

  async getOverview(tenantId: string): Promise<ServiceOverview> {
    const jobs = this.jobs.filter((job) => job.tenantId === tenantId);
    return {
      planningCount: jobs.filter((job) => job.bucket === 'planning').length,
      issuedCount: jobs.filter((job) => job.bucket === 'issued').length,
      paperReturnedCount: jobs.filter((job) => job.bucket === 'returned').length,
      approvalPendingCount: jobs.filter((job) => job.status === 'approval-pending').length,
      billingReadyCount: jobs.filter((job) => job.status === 'billing-ready').length,
    };
  }

  async searchJobs(input: ServiceSearchInput): Promise<ServicePage> {
    const normalized = normalizeServiceSearchInput(input);
    const query = normalized.query.toLocaleLowerCase('de-DE');
    const matching = this.jobs.filter((job) => (
      job.tenantId === normalized.tenantId
      && (normalized.bucket === 'all' || job.bucket === normalized.bucket)
      && (normalized.executionMode === 'all' || job.executionMode === normalized.executionMode)
      && (normalized.kind === 'all' || job.kind === normalized.kind)
      && (!query || [job.serviceNumber, job.orderNumber, job.customerName, job.machineLabel, job.technicianName, job.title]
        .join(' ').toLocaleLowerCase('de-DE').includes(query))
    ));
    const start = (normalized.page - 1) * normalized.pageSize;
    return { items: clone(matching.slice(start, start + normalized.pageSize)), total: matching.length, page: normalized.page, pageSize: normalized.pageSize };
  }

  async listPlannableOrders(tenantId: string): Promise<readonly PlannableServiceOrder[]> {
    const seed = this.plannableOrders[tenantId] ?? [];
    // Schon eingeplante Vorgaenge (es gibt bereits einen Job zur orderId) fallen raus.
    const planned = new Set(this.jobs.filter((job) => job.tenantId === tenantId).map((job) => job.orderId));
    return clone(seed.filter((order) => !planned.has(order.orderId)));
  }

  async listUvvDue(tenantId: string): Promise<readonly UvvDueRecord[]> {
    return clone(this.uvvDue[tenantId] ?? []);
  }

  async getJob(tenantId: string, serviceJobId: string): Promise<ServiceJobRecord> {
    const job = this.jobs.find((candidate) => candidate.tenantId === tenantId && candidate.id === serviceJobId);
    if (!job) throw new Error('Serviceauftrag wurde nicht gefunden.');
    return clone(job);
  }

  async listTechnicians(_tenantId: string): Promise<readonly ServiceTechnicianOption[]> {
    return clone(technicians);
  }

  async planJob(rawInput: PlanServiceJobInput): Promise<ServiceJobRecord> {
    const input = normalizePlanServiceJobInput(rawInput);
    const existingIndex = input.serviceJobId
      ? this.jobs.findIndex((job) => job.tenantId === input.tenantId && job.id === input.serviceJobId)
      : -1;
    if (existingIndex >= 0) {
      const current = this.jobs[existingIndex];
      const status = current.status === 'new'
        ? (input.executionMode === 'field' ? 'planned' : 'pickup-planned')
        : current.status;
      const next: ServiceJobRecord = {
        ...current,
        executionMode: input.executionMode,
        kind: input.kind,
        priority: input.priority,
        scheduledStart: input.scheduledStart,
        scheduledEnd: input.scheduledEnd,
        technicianName: input.technicianName,
        workDescription: input.workDescription,
        address: input.serviceAddress,
        internalNotes: input.internalNotes ?? '',
        status,
        bucket: serviceStatusBucket(status),
        updatedAt: now(),
      };
      this.jobs[existingIndex] = next;
      return clone(next);
    }
    const serial = String(this.jobs.filter((job) => job.tenantId === input.tenantId).length + 1).padStart(6, '0');
    const status = input.executionMode === 'field' ? 'planned' : 'pickup-planned';
    const created = seedJob(input.tenantId, serial, {
      id: crypto.randomUUID(),
      orderId: input.orderId,
      orderNumber: input.orderId,
      serviceNumber: `S-26-${serial}`,
      title: input.kind === 'repair' ? 'Reparatur' : input.kind === 'maintenance' ? 'Wartung' : 'UVV-Prüfung',
      executionMode: input.executionMode,
      kind: input.kind,
      priority: input.priority,
      scheduledStart: input.scheduledStart,
      scheduledEnd: input.scheduledEnd,
      technicianName: input.technicianName,
      workDescription: input.workDescription,
      address: input.serviceAddress,
      internalNotes: input.internalNotes ?? '',
      status,
      bucket: serviceStatusBucket(status),
    });
    this.jobs.unshift(created);
    return clone(created);
  }

  async createPrint(input: CreateServicePrintInput): Promise<ServicePrintRecord> {
    const jobIndex = this.jobs.findIndex((job) => job.tenantId === input.tenantId && job.id === input.serviceJobId);
    if (jobIndex < 0) throw new Error('Serviceauftrag wurde nicht gefunden.');
    const job = this.jobs[jobIndex];
    if (!job.scheduledStart) throw new Error('Vor dem Druck muss ein Termin geplant sein.');
    if (!job.workDescription.trim()) throw new Error('Vor dem Druck wird eine Arbeitsbeschreibung benötigt.');
    this.prints = this.prints.map((record) => record.serviceJobId === job.id && record.status !== 'superseded'
      ? { ...record, status: 'superseded' }
      : record);
    const version = this.prints.filter((record) => record.serviceJobId === job.id).length + 1;
    const print: ServicePrintRecord = {
      id: crypto.randomUUID(),
      serviceJobId: job.id,
      documentNumber: `WA-${job.serviceNumber.slice(2)}`,
      version,
      status: 'generated',
      snapshot: snapshotOf(job),
      createdAt: now(),
      printedAt: null,
    };
    this.prints.push(print);
    const status = job.executionMode === 'field' && ['new', 'planned'].includes(job.status) ? 'printed' : job.status;
    this.jobs[jobIndex] = { ...job, latestPrint: print, status, bucket: serviceStatusBucket(status), updatedAt: now() };
    return clone(print);
  }

  async markPrintAsPrinted(input: MarkServicePrintInput): Promise<ServicePrintRecord> {
    const printIndex = this.prints.findIndex((record) => record.id === input.printDocumentId && record.serviceJobId === input.serviceJobId);
    if (printIndex < 0) throw new Error('Druckdokument wurde nicht gefunden.');
    const jobIndex = this.jobs.findIndex((job) => job.id === input.serviceJobId && job.tenantId === input.tenantId);
    if (jobIndex < 0) throw new Error('Serviceauftrag wurde nicht gefunden.');
    const print = { ...this.prints[printIndex], status: 'printed' as const, printedAt: now() };
    this.prints[printIndex] = print;
    const job = this.jobs[jobIndex];
    this.jobs[jobIndex] = { ...job, latestPrint: print, updatedAt: now() };
    return clone(print);
  }

  async capturePaperReturn(rawInput: CaptureServicePaperReturnInput): Promise<ServiceJobRecord> {
    const input = normalizePaperReturnInput(rawInput);
    const index = this.jobs.findIndex((job) => job.tenantId === input.tenantId && job.id === input.serviceJobId);
    if (index < 0) throw new Error('Serviceauftrag wurde nicht gefunden.');
    const job = this.jobs[index];
    const returnedAt = now();
    const paperReturn = {
      id: crypto.randomUUID(),
      serviceJobId: job.id,
      printDocumentId: input.printDocumentId ?? job.latestPrint?.id ?? null,
      receivedAt: returnedAt,
      technicianName: input.technicianName,
      arrivalTime: input.arrivalTime ?? '',
      departureTime: input.departureTime ?? '',
      travelHours: input.travelHours ?? 0,
      workHours: input.workHours ?? 0,
      kilometers: input.kilometers ?? 0,
      workPerformed: input.workPerformed,
      materials: input.materials ?? [],
      completed: input.completed,
      followUpRequired: input.followUpRequired,
      followUpNotes: input.followUpNotes ?? '',
      customerName: input.customerName ?? '',
      customerSignatureOnPaper: input.customerSignatureOnPaper,
      officeNotes: input.officeNotes ?? '',
    };
    const status: ServiceStatus = job.executionMode === 'field' ? 'paper-returned' : 'returned';
    const next = { ...job, paperReturn, status, bucket: serviceStatusBucket(status), updatedAt: returnedAt };
    this.jobs[index] = next;
    return clone(next);
  }

  async setApproval(input: SetServiceApprovalInput): Promise<ServiceJobRecord> {
    const index = this.jobs.findIndex((job) => job.tenantId === input.tenantId && job.id === input.serviceJobId);
    if (index < 0) throw new Error('Serviceauftrag wurde nicht gefunden.');
    const job = this.jobs[index];
    if (job.executionMode !== 'workshop') throw new Error('Eine Reparaturfreigabe ist nur für Werkstattaufträge vorgesehen.');
    const status = input.status === 'approved' ? 'approved' : input.status === 'declined' ? 'declined' : 'approval-pending';
    const next: ServiceJobRecord = {
      ...job,
      status,
      bucket: serviceStatusBucket(status),
      approval: {
        status: input.status,
        approvedAmount: input.approvedAmount ?? null,
        decidedAt: input.status === 'pending' ? null : now(),
        decidedByName: input.decidedByName.trim(),
        channel: input.channel,
        notes: input.notes?.trim() ?? '',
      },
      updatedAt: now(),
    };
    this.jobs[index] = next;
    return clone(next);
  }

  async saveEstimate(input: SaveServiceEstimateInput): Promise<ServiceJobRecord> {
    const index = this.jobs.findIndex((job) => job.tenantId === input.tenantId && job.id === input.serviceJobId);
    if (index < 0) throw new Error('Serviceauftrag wurde nicht gefunden.');
    validateServiceEstimateInput(input);
    const job = this.jobs[index];
    const lines = priceServiceEstimateLines(input.lines);
    const totals = calculateServiceEstimateTotals(lines);
    // Kostenvoranschlag-Nummer aus der Serviceauftragsnummer abgeleitet (ein KV je Auftrag) -- kein
    // eigener Nummernkreis nötig. S-26-000012 -> KV-26-000012.
    const estimateNumber = job.estimate?.estimateNumber ?? `KV-${job.serviceNumber.replace(/^S-?/i, '')}`;
    const estimate: ServiceEstimateRecord = {
      estimateNumber,
      lines,
      ...totals,
      note: input.note?.trim() ?? '',
      createdAt: job.estimate?.createdAt ?? now(),
    };
    // Werkstattauftrag: mit dem Kostenvoranschlag ist die Kostenschätzung fertig -> Status vorziehen.
    const status: ServiceStatus = job.executionMode === 'workshop' && job.status === 'diagnosis' ? 'estimate-ready' : job.status;
    const next: ServiceJobRecord = {
      ...job, estimate, status, bucket: serviceStatusBucket(status), updatedAt: now(),
    };
    this.jobs[index] = next;
    return clone(next);
  }

  async transitionJob(input: TransitionServiceJobInput): Promise<ServiceJobRecord> {
    const index = this.jobs.findIndex((job) => job.tenantId === input.tenantId && job.id === input.serviceJobId);
    if (index < 0) throw new Error('Serviceauftrag wurde nicht gefunden.');
    const current = this.jobs[index];
    assertServiceTransition(current.executionMode, current.status, input.toStatus);
    const next = { ...current, status: input.toStatus, bucket: serviceStatusBucket(input.toStatus), updatedAt: now() };
    this.jobs[index] = next;
    return clone(next);
  }
}

export function createDemoServiceGateway(): ServiceGateway {
  return new DemoServiceGateway();
}
